import { Injectable } from '@angular/core';
import { buildConsultationData } from '@app/shared/utils/static-helpers/consultation-helper';
import { translateKey } from '@app/shared/utils/static-helpers/translate';
import { AppState } from '@app/store';
import { createCurrentConsultation, createCurrentConsultationFromShareRecoPopin } from '@app/store/consultation/consultation.actions';
import { selectConsultation, selectConsultationRenalDetectPrediction } from '@app/store/consultation/consultation.selectors';
import { selectCurrentBigMeasurementUnit, selectVet } from '@app/store/vet';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { switchMap, withLatestFrom } from 'rxjs/operators';
import {
  createAllowanceConsultation,
  createAllowanceConsultationFromShareRecoPopin,
  resetAllowanceSelectedProductId,
} from '../actions/allowance-consultation.actions';
import { selectAllowanceCurrentProgram } from '../selectors/allowance-program.selectors';
import { selectAllowance } from '../selectors/allowance.selectors';
import { selectSelectedProducts, selectSelectedPackIds } from '@app/store/products/products.selectors';
import { resetSelectedProduct } from '@app/store/products/products.actions';
import { Tool } from '@app/shared/utils';
import { selectIsPetInLactation } from '../selectors/allowance-params.selectors';

@Injectable()
export class AllowanceConsultationEffects {
  /**
   * Create consultation
   * Prepare the data for creating consultation inside consultation store
   */
  createAllowanceConsultation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createAllowanceConsultation),
      withLatestFrom(
        this.store$.select(selectConsultation),
        this.store$.select(selectSelectedPackIds),
        this.store$.select(selectAllowance),
        this.store$.select(selectConsultationRenalDetectPrediction),
        this.store$.select(selectSelectedProducts),
        this.store$.select(selectAllowanceCurrentProgram),
        this.store$.select(selectVet),
        this.store$.select(selectCurrentBigMeasurementUnit),
        this.store$.select(selectIsPetInLactation),
      ),
      switchMap(
        ([
          { owner, tool: toolToOverride },
          consultationState,
          selectedPackIds,
          allowanceState,
          prediction,
          products,
          program,
          vet,
          currentBigMeasurementUnit,
          isLactation,
        ]) => {
          const petInfo = allowanceState?.allowanceParams?.deprecatedPetInfo;
          return [
            createCurrentConsultation({
              consultationData: buildConsultationData({
                consultationState,
                allowanceState,
                selectedPackIds,
                prediction,
                products,
                program,
                vet,
                tool: toolToOverride,
                currentBigMeasurementUnit,
                isLactation,
              }),
              petInfo,
              createPatientPopinTitle: toolToOverride === Tool.WeightManagement && translateKey('action_create-wlp'),
              owner,
            }),
          ];
        },
      ),
    ),
  );

  /**
   * Create consultation from Share Reco popin
   * Prepare the data for creating consultation inside consultation store
   */
  createAllowanceConsultationFromShareRecoPopin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createAllowanceConsultationFromShareRecoPopin),
      withLatestFrom(
        this.store$.select(selectConsultation),
        this.store$.select(selectSelectedPackIds),
        this.store$.select(selectAllowance),
        this.store$.select(selectConsultationRenalDetectPrediction),
        this.store$.select(selectSelectedProducts),
        this.store$.select(selectAllowanceCurrentProgram),
        this.store$.select(selectVet),
        this.store$.select(selectCurrentBigMeasurementUnit),
        this.store$.select(selectIsPetInLactation),
      ),
      switchMap(
        ([
          { formValues },
          consultationState,
          selectedPackIds,
          allowanceState,
          prediction,
          products,
          program,
          vet,
          currentBigMeasurementUnit,
          isLactation,
        ]) => [
          createCurrentConsultationFromShareRecoPopin({
            formValues,
            consultationData: buildConsultationData({
              consultationState,
              allowanceState,
              selectedPackIds,
              prediction,
              products,
              program,
              vet,
              tool: allowanceState?.allowanceParams?.tool,
              currentBigMeasurementUnit,
              isLactation,
            }),
            petInfo: allowanceState?.allowanceParams?.deprecatedPetInfo,
          }),
        ],
      ),
    ),
  );

  /**
   * Listen to products store resetSelectedProduct action
   * and trigger allowance store resetAllowanceSelectedProductId action
   */
  resetAllowanceSelectedProductOnProductReset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resetSelectedProduct),
      switchMap(({ productId }) => [resetAllowanceSelectedProductId({ productId })]),
    ),
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store$: Store<AppState>,
  ) {}
}
